import ExcelJS from "exceljs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import isGHMC, {
  formatDateTimeWithoutSec,
  getCurrentDateTime,
} from "../../../../Common/helper/helpar";
import { TransferstationManagementStore } from "./store/transferstationManagementStore";

export const DetailedReportTSSCTPExport = ({ data }) => {
  const isProd = isGHMC();
  const { currentFilters } = TransferstationManagementStore();
  const fromDate = currentFilters?.FromDate;
  const toDate = currentFilters?.ToDate;
  const currentDate = getCurrentDateTime();

  /* ================= GROUP BY DATE ================= */
  const groupByDate = (data) => {
    const map = {};
    (data || []).forEach((item) => {
      const date = item.ReportDate;
      if (!map[date]) map[date] = [];
      map[date].push(item);
    });
    return map;
  };

  const groupedByDate = groupByDate(data || []);
  const dateKeys = Object.keys(groupedByDate).sort();

  /* ================= UNIQUE LOCATIONS ================= */
  const locations = Array.from(
    new Map(
      (data || []).map((item) => [item.TransferStationId, item]),
    ).values(),
  );

  /* ================= METRIC FIELDS ================= */
  const excludedFields = [
    "ReportDate",
    "TransferStationId",
    "TransferStationName",
    "StationType",
    "Total",
  ];

  const metricFields = Array.from(
    new Set(
      (data || []).flatMap((item) =>
        Object.keys(item).filter((k) => !excludedFields.includes(k)),
      ),
    ),
  );

  /* ================= HELPERS ================= */
  const formatDateOnly = (dateString) => {
    if (!dateString) return "";
    try {
      const datePart = dateString.split(" ")[0];
      const date = new Date(datePart);
      if (isNaN(date)) return dateString;

      return date
        .toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
        .replace(/\//g, "-");
    } catch {
      return dateString;
    }
  };

  const getValue = (stationId, date, field) => {
    const record = groupedByDate[date]?.find(
      (r) => r.TransferStationId === stationId,
    );
    return record?.[field] ?? 0;
  };

  const getTotal = (stationId, date) => {
    return metricFields.reduce(
      (sum, field) => sum + Number(getValue(stationId, date, field)),
      0,
    );
  };

  const getGrandTotalForField = (date, field) => {
    return locations.reduce(
      (sum, location) =>
        sum + Number(getValue(location.TransferStationId, date, field)),
      0,
    );
  };

  const getGrandTotalForDate = (date) => {
    return locations.reduce(
      (sum, location) =>
        sum + Number(getTotal(location.TransferStationId, date)),
      0,
    );
  };

  /* ================= EXCEL EXPORT ================= */
  /* ================= EXCEL EXPORT ================= */
  const downloadAsExcel = async () => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(
    "Transferstation Management Monthly Report"
  );

  const bold = { bold: true };

  // Only 3 fixed columns
  const totalCols = 3 + dateKeys.length * (metricFields.length + 1);

  /* ================= HEADINGS ================= */

  worksheet.addRow([""]);

  const heading1 = worksheet.addRow([
    `Transferstation Management Monthly Report Generated on: ${formatDateTimeWithoutSec(currentDate)}`
  ]);
  worksheet.mergeCells(2, 1, 2, totalCols);
  heading1.getCell(1).font = { ...bold, size: 14 };
  heading1.getCell(1).alignment = { horizontal: "center" };

  const heading2 = worksheet.addRow([
    `Transferstation Management Monthly Report as on (${fromDate || ""} to ${toDate || ""})`
  ]);
  worksheet.mergeCells(3, 1, 3, totalCols);
  heading2.getCell(1).font = { ...bold, size: 12 };
  heading2.getCell(1).alignment = { horizontal: "center" };

  worksheet.addRow([""]);

  /* ================= HEADER ROW 1 ================= */

  const headerRow1 = ["S.No", "Location Name", "TS/SCTP/RFC"];

  dateKeys.forEach((date) => {
    headerRow1.push(formatDateOnly(date));
    for (let i = 0; i < metricFields.length; i++) {
      headerRow1.push("");
    }
    headerRow1.push("");
  });

  const header1 = worksheet.addRow(headerRow1);

  let colIndex = 4;
  const headerRow1Index = 5;

  dateKeys.forEach((date) => {
    worksheet.mergeCells(
      headerRow1Index,
      colIndex,
      headerRow1Index,
      colIndex + metricFields.length
    );

    header1.getCell(colIndex).value = formatDateOnly(date);
    header1.getCell(colIndex).font = bold;
    header1.getCell(colIndex).alignment = { horizontal: "center" };

    colIndex += metricFields.length + 1;
  });

  /* ================= HEADER ROW 2 ================= */

  const headerRow2 = ["", "", ""];

  dateKeys.forEach(() => {
    metricFields.forEach((field) => headerRow2.push(field));
    headerRow2.push("Total");
  });

  const header2 = worksheet.addRow(headerRow2);

  /* ================= STYLE HEADERS ================= */

  [header1, header2].forEach((row) => {
    row.eachCell((cell) => {
      cell.font = { ...bold, color: { argb: "FFFFFFFF" } };
      cell.alignment = { horizontal: "center", vertical: "middle" };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "F6B1B1" }
      };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" }
      };
    });
  });

  /* ================= HELPER FUNCTIONS ================= */

  const isDescriptionField = (field) =>
    field.toLowerCase().includes("description");

  const getValue = (stationId, date, field) => {
    const record = groupedByDate[date]?.find(
      (r) => r.TransferStationId === stationId
    );

    const val = record?.[field];

    // ✅ If Description → return text
    if (isDescriptionField(field)) {
      return val ?? "";
    }

    // ✅ Otherwise numeric
    const num = Number(val);
    return isNaN(num) ? 0 : num;
  };

  const getTotal = (stationId, date) => {
    return metricFields.reduce((sum, field) => {
      if (isDescriptionField(field)) return sum;
      return sum + getValue(stationId, date, field);
    }, 0);
  };

  const getGrandTotalForField = (date, field) => {
    if (isDescriptionField(field)) return "";

    return locations.reduce(
      (sum, loc) =>
        sum + getValue(loc.TransferStationId, date, field),
      0
    );
  };

  const getGrandTotalForDate = (date) => {
    return locations.reduce(
      (sum, loc) =>
        sum + getTotal(loc.TransferStationId, date),
      0
    );
  };

  /* ================= DATA ROWS ================= */

  locations.forEach((location, index) => {
    const rowData = [
      index + 1,
      location.TransferStationName,
      location.StationType || "TS"
    ];

    dateKeys.forEach((date) => {
      metricFields.forEach((field) =>
        rowData.push(
          getValue(location.TransferStationId, date, field)
        )
      );

      rowData.push(
        getTotal(location.TransferStationId, date)
      );
    });

    worksheet.addRow(rowData);
  });

  /* ================= GRAND TOTAL ROW ================= */

  const totalRow = ["", "Grand Total", ""];

  dateKeys.forEach((date) => {
    metricFields.forEach((field) =>
      totalRow.push(getGrandTotalForField(date, field))
    );
    totalRow.push(getGrandTotalForDate(date));
  });

  const totalExcelRow = worksheet.addRow(totalRow);

  totalExcelRow.eachCell((cell) => {
    cell.font = { ...bold, color: { argb: "FFFFFFFF" } };
    cell.alignment = { horizontal: "center", vertical: "middle" };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "F6B1B1" }
    };
    cell.border = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" }
    };
  });

  /* ================= COLUMN WIDTHS ================= */

  const columnWidths = [8, 25, 15];

  dateKeys.forEach(() => {
    metricFields.forEach(() => columnWidths.push(18));
    columnWidths.push(15);
  });

  worksheet.columns = columnWidths.map((w) => ({ width: w }));

  /* ================= SAVE FILE ================= */

  const buffer = await workbook.xlsx.writeBuffer();

  const blob = new Blob([buffer], {
    type:
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  });

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "TransferstationManagementMonthlyReport.xlsx";
  a.click();
  URL.revokeObjectURL(url);
};


  /* ================= PDF EXPORT ================= */
  const downloadAsPDF = () => {
    const doc = new jsPDF("l", "mm", "a4");

    doc.text(
      "Transferstation Management Monthly Report",
      doc.internal.pageSize.width / 2,
      15,
      { align: "center" },
    );

    const tableData = [];

    /* ===== HEADERS ===== */
    const headerRow1 = ["S.No", "Location Name", "TS/SCTP/RFC", "Description"];

    dateKeys.forEach((date) => {
      headerRow1.push(formatDateOnly(date));

      for (let i = 0; i < metricFields.length; i++) {
        headerRow1.push("");
      }

      headerRow1.push("");
    });

    const headerRow2 = ["", "", "", ""];

    dateKeys.forEach(() => {
      metricFields.forEach((f) => headerRow2.push(f));
      headerRow2.push("Total");
    });

    /* ===== DATA ===== */
    locations.forEach((location, index) => {
      const row = [
        index + 1,
        location.TransferStationName,
        location.StationType || "TS",
        location.Description || "",
      ];

      dateKeys.forEach((date) => {
        metricFields.forEach((field) =>
          row.push(getValue(location.TransferStationId, date, field)),
        );

        row.push(getTotal(location.TransferStationId, date));
      });

      tableData.push(row);
    });

    /* ===== TOTAL ===== */
    const totalRow = ["", "", "Grand Total", ""];

    dateKeys.forEach((date) => {
      metricFields.forEach((field) =>
        totalRow.push(getGrandTotalForField(date, field)),
      );

      totalRow.push(getGrandTotalForDate(date));
    });

    tableData.push(totalRow);

    autoTable(doc, {
      head: [headerRow1, headerRow2],
      body: tableData,
      startY: 25,
      styles: { fontSize: 7, halign: "center" },
      headStyles: {
        fillColor: isProd ? [13, 93, 165] : [236, 17, 23],
        textColor: [255, 255, 255],
        fontStyle: "bold",
      },
    });

    doc.save("TransferstationManagementMonthlyReport.pdf");
  };

  return (
    <div className="d-flex justify-content-end mt-3">
      <button className="btn btn-excel" onClick={downloadAsExcel}>
        <i className="fa-solid fa-file-excel me-1"></i> Excel
      </button>

      {/* <button className="btn btn-pdf ms-2" onClick={downloadAsPDF}>
        PDF
      </button> */}
    </div>
  );
};
